// Ambient animated background (Alex 2026-06-23) — artistic loop videos from the
// Storage "BGMOVIES" collection (arkserver host), played heavily blurred behind
// a glassmorphism frost so the app gets a calm moving backdrop while idle.
//
// The 5 curated clips (forest / smoke / clouds / seashore / person-at-sea). We
// reference them by storage media id and resolve the HLS master at runtime via
// the public X-HLS-URL header (canonical — never construct, see memory note).
// The /storage/media/{id} endpoint is public (no API key), as is the m3u8.
export const BG_HOST = 'https://api-storage.arkserver.arkturian.com';
export const BG_VIDEO_IDS = [36461, 36462, 36463, 36464, 36465];

const KEY = 'cloud-bg-video';
// Glass/veil intensity (Alex 2026-07-15): how OPAQUE the frost over the ambient
// is. Higher = more surface tint = calmer/more readable but less ambient shows;
// lower = more transparent = ambient comes through strongly. Drives the CSS vars
// the ambient layers consume (BackgroundVideo.css). Default 58 = current balance.
const VEIL_KEY = 'cloud-bg-veil';
// MIN = 0 (Alex 2026-07-15): slider fully left = the RAW video — no blur, no
// tint, completely visible. Text stays readable via the per-glyph halo.
export const VEIL_MIN = 0;
export const VEIL_MAX = 92;
export const VEIL_DEFAULT = 58;

// Blur is its OWN slider now (Alex 2026-07-15: "zwei Regler — Blur und
// Transparency"). The old "Kräftige Leisten" solid-bars toggle is gone — it
// planted a fixed 72% white tint on header/footer that fought the sliders
// (the mysterious "default weiss" header). Everything flows; the two sliders
// are the single source of glass truth.
const BLUR_KEY = 'cloud-bg-blur';
export const BLUR_MIN = 0;
export const BLUR_MAX = 100;
export const BLUR_DEFAULT = 58;

export function bgEnabled(): boolean {
  return localStorage.getItem(KEY) === '1';
}

export function bgBlur(): number {
  const v = parseInt(localStorage.getItem(BLUR_KEY) || '', 10);
  if (!Number.isFinite(v)) return BLUR_DEFAULT;
  return Math.min(BLUR_MAX, Math.max(BLUR_MIN, v));
}

// Blur strength → video blur + panel backdrop blur/saturation.
// 0 = raw video + crystal panels · 58 ≈ the original look (55px / 14px / 1.5).
export function applyBlur(b: number): void {
  const blur = Math.min(BLUR_MAX, Math.max(BLUR_MIN, b));
  const root = document.documentElement.style;
  root.setProperty('--ambient-blur', `${Math.round(blur * 0.95)}px`);
  root.setProperty('--glass-blur', `${Math.round(blur * 0.24)}px`);
  root.setProperty('--glass-sat', (1 + blur / 116).toFixed(2));
}

export function setBgBlur(b: number): void {
  const clamped = Math.min(BLUR_MAX, Math.max(BLUR_MIN, Math.round(b)));
  localStorage.setItem(BLUR_KEY, String(clamped));
  applyBlur(clamped);
}

export function bgVeil(): number {
  const v = parseInt(localStorage.getItem(VEIL_KEY) || '', 10);
  if (!Number.isFinite(v)) return VEIL_DEFAULT;
  return Math.min(VEIL_MAX, Math.max(VEIL_MIN, v));
}

export function applyBg(on: boolean): void {
  document.documentElement.dataset.bg = on ? 'on' : 'off';
}

// Push the intensity into CSS vars. The content-reading veil = the value; the
// full-viewport frost tracks a bit lighter so the frame keeps a touch more
// ambient than the reading area.
// Transparency slider → tint only (veil over content + full-viewport frost).
// 0 = no tint at all (video fully visible) · 92 = heavy milk tint.
export function applyVeil(v: number): void {
  const veil = Math.min(VEIL_MAX, Math.max(VEIL_MIN, v));
  const frost = Math.max(0, veil - 3);
  const root = document.documentElement.style;
  root.setProperty('--ambient-veil', `${veil}%`);
  root.setProperty('--ambient-frost', `${frost}%`);
}

export function setBgEnabled(on: boolean): void {
  localStorage.setItem(KEY, on ? '1' : '0');
  applyBg(on);
  window.dispatchEvent(new CustomEvent('cloud-bg-change', { detail: on }));
}

export function setBgVeil(v: number): void {
  const clamped = Math.min(VEIL_MAX, Math.max(VEIL_MIN, Math.round(v)));
  localStorage.setItem(VEIL_KEY, String(clamped));
  applyVeil(clamped);
}

export function initBgVideo(): void {
  applyBg(bgEnabled());
  applyVeil(bgVeil());
  applyBlur(bgBlur());
}
